import { 
    Directive, 
    ElementRef, 
    OnDestroy, 
    inject, 
    signal,
    input,
    OnInit,
    model
} from '@angular/core';
import { NotificationService } from '@shared/services/notification.service';

const HEADER_HEIGHT_PX = 35; // must match $header-height in base-widget.scss
const BOTTOM_TOLERANCE_PX = 30; // When dragging the bottom edge, if mouseup occurs within this many pixels of the parent's bottom, convert to fillRemaining.
const STORAGE_PREFIX = 'oms.widget.';
const KEY_FILL_REMAINING = (id: string) => `${STORAGE_PREFIX}fill-remaining.${id}`;
const KEY_COLLAPSED = (id: string) => `${STORAGE_PREFIX}collapsed.${id}`;
const KEY_HEIGHT = (id: string) => `${STORAGE_PREFIX}height.${id}`;
const KEY_PREV_HEIGHT = (id: string) => `${STORAGE_PREFIX}prev-height.${id}`;

@Directive({
    selector: '[appBaseWidget]',
    exportAs: 'baseWidget',
    host: {
        '[class.has-specific-height]': 'fixedSize() ? defaultHeight() > 0 : heightFraction() > 0',
        '[class.has-fixed-height]': 'fixedSize()',
        '[class.fill-remaining]': 'fillRemaining()',
        '[style.--widget-height]': 'fixedSize() ? (defaultHeight() > 0 ? defaultHeight() + "px" : undefined) : (heightFraction() > 0 ? (heightFraction() * 100).toFixed(2) + "%" : undefined)',
        '(document:mousemove)': 'onMouseMove($event)',
        '(document:mouseup)': 'onMouseUp($event)',
        '(cdkDragStarted)': 'onCdkDragStarted()',
        '(cdkDragEnded)': 'onCdkDragEnded()'
    }
})
export class BaseWidgetDirective implements OnInit, OnDestroy {
    readonly fillRemaining = model<boolean>(false);
    readonly fixedSize = input<boolean>(false);
    readonly defaultHeight = input<number>(0); // px, used only when fixedSize() is true
    readonly defaultFraction = input<number>(0); // fraction of column height (0–1), used when fixedSize() is false
    readonly widgetId = input<string>('');

    private readonly _el = inject(ElementRef);
    private readonly _notification = inject(NotificationService);

    public heightFraction = signal<number>(0); // fraction of column height (0–1), persisted to storage
    public isCollapsed = signal<boolean>(false);

    /** px height for drag arithmetic and CDK placeholder sizing only — not used for CSS layout. */
    public heightPx(): number {
        if (this.fixedSize()) return this.defaultHeight();
        const f = this.heightFraction();
        if (f <= 0) return 0;
        const colH = this._getColumnHeight();
        return colH > 0 ? Math.round(f * colH) : 0;
    }

    /** Effective px height for CDK placeholder: current height or default fallback. */
    public defaultHeightPx(): number {
        if (this.fixedSize()) return this.defaultHeight();
        const px = this.heightPx();
        if (px > 0) return px;
        return Math.round(this.defaultFraction() * this._getColumnHeight());
    }

    // map of widgetId -> directive instance for live updates
    private static readonly _widgetRegistry = new Map<string, BaseWidgetDirective>();
    private _isDragging = false;
    private _dragStartY = 0;
    private _siblingWidgets: BaseWidgetDirective[] = [];
    private _myDragIndex = 0;
    private _startHeights: number[] = [];
    private _heightBeforeCollapse: number | undefined = undefined;
    private _prevCollapsedStates: boolean[] = [];
    private _storageErrorShown = false;

    public ngOnInit(): void {
        // Expose the widget id on the host element so sibling discovery can
        // map DOM children back to directive instances via the `_widgetRegistry` map.
        try { (this._el.nativeElement as HTMLElement).dataset.widgetId = this.widgetId(); } catch {}
        BaseWidgetDirective._widgetRegistry.set(this.widgetId(), this);
        this._loadSettingsFromStorage();
    }

    public ngOnDestroy(): void {
        // Only remove dataset / registry entry if this instance still owns them.
        // After a CDK drag-drop Angular may call ngOnInit of the new instance
        // before ngOnDestroy of the old one; blindly deleting would wipe the
        // new instance's registration.
        try {
            if ((this._el.nativeElement as HTMLElement).dataset.widgetId === this.widgetId()) {
                delete (this._el.nativeElement as HTMLElement).dataset.widgetId;
            }
        } catch {}
        if (BaseWidgetDirective._widgetRegistry.get(this.widgetId()) === this) {
            BaseWidgetDirective._widgetRegistry.delete(this.widgetId());
        }

        // ensure global dragging class is removed when directive is destroyed
        try { document.documentElement.classList.remove('oms-dragging'); } catch {}

        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    }

    /**
     * Clear the persisted and live `fillRemaining` state for the given widget id.
     * If the directive instance exists, update its signal immediately
     */
    public static clearFillRemaining(widgetId: string): void {
        if (!widgetId) return;

        const widget = BaseWidgetDirective._widgetRegistry.get(widgetId);
        if (widget?.fillRemaining()) {
            widget.fillRemaining.set(false);
            widget.heightFraction.set(widget.defaultFraction());
            try { widget._safeSet(KEY_FILL_REMAINING(widget.widgetId()), 'false'); } catch { /* ignore */ }
        }
    }

    /**
     * Returns the expected rendered height of a widget in pixels.
     * `fillRemaining` widgets report their `defaultHeight` since they will be
     * reset to that value after a drag/drop operation.
     */
    public static getEffectiveHeight(widgetId: string): number {
        const widget = BaseWidgetDirective._widgetRegistry.get(widgetId);
        if (!widget) return 0;
        return widget.heightPx() > 0 ? widget.heightPx() : Math.round(widget.defaultFraction() * widget._getColumnHeight());
    }

    /**
     * Clamps the widget's height so it occupies at most `maxAvailablePx` in its column.
     * Call only after the widget is in its final DOM position (e.g. from a requestAnimationFrame
     * callback after a CDK drop) so that `_getColumnHeight()` resolves against the correct column.
     */
    public static fitToColumn(widgetId: string, maxAvailablePx: number): void {
        const widget = BaseWidgetDirective._widgetRegistry.get(widgetId);
        if (!widget || widget.fixedSize()) return;
        const currentPx = widget.heightPx();
        if (currentPx <= maxAvailablePx) return;
        widget._setHeightFromPx(Math.max(HEADER_HEIGHT_PX, maxAvailablePx));
        widget._saveHeight();
    }

    /** Marks a widget as `fillRemaining` so it stretches to consume leftover column space. */
    public static setFillRemaining(widgetId: string): void {
        if (!widgetId) return;
        const widget = BaseWidgetDirective._widgetRegistry.get(widgetId);
        if (!widget || widget.fixedSize() || widget.fillRemaining()) return;
        widget.fillRemaining.set(true);
        widget.heightFraction.set(0);
        try { widget._safeSet(KEY_FILL_REMAINING(widget.widgetId()), 'true'); } catch { /* ignore */ }
    }

    // CDK drag lifecycle hooks (widget host elements have `cdkDrag`)
    public onCdkDragStarted(): void {
        document.documentElement.classList.add('oms-dragging');

        const el = this._el.nativeElement as HTMLElement;
        let needsPlaceholderPatch = false;

        if (this.isCollapsed()) {
            const colH = this._getColumnHeight();
            const h = this._heightBeforeCollapse ?? Math.round(this.defaultFraction() * colH);
            this._heightBeforeCollapse = undefined;
            this.isCollapsed.set(false);
            this._saveCollapsedState(false);
            this._setHeightFromPx(h);
            el.classList.remove('collapsed');
            el.classList.add('has-specific-height');
            el.style.setProperty('--widget-height', `${this.heightPx()}px`);
            needsPlaceholderPatch = true;
        }

        if (this.fillRemaining()) {
            const renderedH = Math.round((this._el.nativeElement as HTMLElement).getBoundingClientRect().height);
            this._setFillRemaining(false);
            this._setHeightFromPx(renderedH); // capture actual rendered size before converting away from fill-remaining
            el.classList.remove('fill-remaining');
            el.classList.add('has-specific-height');
            el.style.setProperty('--widget-height', `${this.heightPx()}px`);
            needsPlaceholderPatch = true;
        }

        if (needsPlaceholderPatch) {
            this._saveHeight();
            const h = this.heightPx();
            const placeholder = document.querySelector<HTMLElement>('.cdk-drag-placeholder');
            if (placeholder) placeholder.style.height = `${h}px`;
            document.documentElement.style.setProperty('--cdk-dragged-height', `${h}px`);
            return;
        }

        const widgetHeight = Math.round((this._el.nativeElement as HTMLElement).getBoundingClientRect().height);
        document.documentElement.style.setProperty(
            '--cdk-dragged-height',
            `${widgetHeight > 0 ? widgetHeight : HEADER_HEIGHT_PX}px`
        );
    }

    public onCdkDragEnded(): void {
        document.documentElement.classList.remove('oms-dragging');
        document.documentElement.style.removeProperty('--cdk-dragged-height');
    }

    public onDragStart(event: MouseEvent): void {
        this._isDragging = true;
        this._dragStartY = event.clientY;

        // Expand and clear fillRemaining on THIS widget before snapshotting so the
        // drag starts from the correct logical height. Use HEADER_HEIGHT_PX as the
        // starting height when collapsed so there's no visual jump — the resize drag
        // will grow the widget from its current visible size.
        if (this.isCollapsed()) {
            this._setHeightFromPx(HEADER_HEIGHT_PX);
            this._heightBeforeCollapse = undefined;
            this.isCollapsed.set(false);
            this._saveCollapsedState(false);
        }
        if (this.fillRemaining()) {
            const renderedH = (this._el.nativeElement as HTMLElement).getBoundingClientRect().height;
            this._setHeightFromPx(Math.max(HEADER_HEIGHT_PX, renderedH));
            this.fillRemaining.set(false);
            this._safeSet(KEY_FILL_REMAINING(this.widgetId()), 'false');
        }

        // Component host has display:contents — go up two levels to reach the column element.
        const colEl = (this._el?.nativeElement as HTMLElement)?.parentElement?.parentElement;
        if (!colEl) return;

        const myEl = this._el?.nativeElement as HTMLElement;
        // Each column child is a component host with display:contents; find its inner .widget[data-widget-id].
        const elements: HTMLElement[] = [];
        for (const hostEl of Array.from(colEl.children) as HTMLElement[]) {
            const widgetEl = hostEl.querySelector<HTMLElement>('[data-widget-id]');
            if (widgetEl) elements.push(widgetEl);
        }
        const widgets: BaseWidgetDirective[] = [];
        let myIndex = -1;

        elements.forEach((child) => {
            const widgetId = child.dataset?.widgetId;
            const widget = widgetId ? BaseWidgetDirective._widgetRegistry.get(widgetId) : undefined;
            if (widget) {
                if (child === myEl) myIndex = widgets.length;
                widgets.push(widget);
            }
        });

        if (!widgets.length) return;

        // For fixed-size widgets the handle acts as a splitter between the widget
        // above and the widget below: redirect the drag index to the widget above
        // so the growth/shrink logic leaves this widget untouched.
        const effectiveIndex = this.fixedSize() ? myIndex - 1 : myIndex;
        if (effectiveIndex < 0) return; // fixed-size with nothing above — nothing to resize

        this._siblingWidgets = widgets;
        this._myDragIndex = effectiveIndex;

        // Snapshot start heights from actual rendered sizes — getBoundingClientRect() for
        // every widget, including this one, to avoid subpixel rounding mismatches vs heightPx().
        this._startHeights = elements.map(ch =>
            Math.round(ch.getBoundingClientRect().height)
        );

        // Preserve collapsed states to restore on mouseup (this widget is already false).
        this._prevCollapsedStates = widgets.map(w => w.isCollapsed());

        // Lock heights for all siblings at their current rendered heights so flex
        // won't redistribute during the drag. For widgets that were previously
        // `fillRemaining`, clear that flag (and its persisted setting) so the
        // directive exclusively controls heights while dragging.
        widgets.forEach((w, i) => {
            const measured = Math.max(HEADER_HEIGHT_PX, this._startHeights[i] ?? HEADER_HEIGHT_PX);
            w._setHeightFromPx(measured);
            if (w.fillRemaining()) {
                w.fillRemaining.set(false);
                this._safeSet(KEY_FILL_REMAINING(w.widgetId()), 'false');
            }
        });

        document.body.style.cursor = 'ns-resize';
        document.body.style.userSelect = 'none';
    }

    public onMouseUp(event: MouseEvent): void {
        if (!this._isDragging) return;

        this._isDragging = false;
        // Restore collapsed state saved at drag start
        if (this._prevCollapsedStates?.length === this._siblingWidgets.length) {
            this._siblingWidgets.forEach((w, i) => {
                const prev = this._prevCollapsedStates[i];
                if (prev !== undefined) w.isCollapsed.set(prev);
            });
        }

        // If this widget is the last in the column and the mouseup occurred
        // at or near the parent's bottom (we allow a small tolerance),
        // convert it to `fillRemaining` so the widget releases back to flex sizing.
        const el = (this._el?.nativeElement as HTMLElement)?.parentElement;
        const parent = el?.parentElement;
        if (parent && this._siblingWidgets.length && this._myDragIndex === this._siblingWidgets.length - 1 && event) {
            const parentRect = parent.getBoundingClientRect();
            if (event.clientY >= parentRect.bottom - BOTTOM_TOLERANCE_PX) {
                this._setFillRemaining(true);
            }
        }

        this._siblingWidgets.forEach(w => {
            w._saveHeight();
            if (w.fillRemaining()) w.heightFraction.set(0); // release back to flex
        });
        this._siblingWidgets = [];
        this._prevCollapsedStates = [];
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
    }

    public onMouseMove(event: MouseEvent): void {
        if (!this._isDragging) return;

        // require a valid drag snapshot
        if (!this._startHeights || !this._siblingWidgets || this._siblingWidgets.length === 0) return;

        const delta = event.clientY - this._dragStartY;
        const me = this._myDragIndex;
        const start = this._startHeights.slice();
        const widgets = this._siblingWidgets;
        const newHeights = start.slice();

        if (delta >= 0) {
            this._handleDragDown(delta, me, start, widgets, newHeights);
        } else {
            this._handleDragUp(delta, me, start, widgets, newHeights);
        }

        // Apply computed heights to widgets
        widgets.forEach((w, i) => {
            // keep collapsed/header min height enforced
            const applied = Math.max(HEADER_HEIGHT_PX, Math.round(newHeights[i] ?? start[i] ?? HEADER_HEIGHT_PX));
            if (w.fillRemaining()) {
                // leave as flex-sized (0) if flagged
                w.heightFraction.set(0);
            } else {
                w._setHeightFromPx(applied);
            }
        });

        document.body.style.cursor = 'ns-resize';
        document.body.style.userSelect = 'none';
    }

    public onHeaderDblClick(): void {
        if (this.isCollapsed()) {
            // Expanding: if persisted fillRemaining was true, restore that state
            const persistedFill = this._safeGet(KEY_FILL_REMAINING(this.widgetId()));
            if (persistedFill === 'true') {
                // restore as fillRemaining immediately
                this.fillRemaining.set(true);
                this.heightFraction.set(0);
                this._heightBeforeCollapse = undefined;
            } else {
                if (this._heightBeforeCollapse) {
                    this._setHeightFromPx(this._heightBeforeCollapse);
                } else {
                    this.heightFraction.set(this.defaultFraction());
                }
                this._heightBeforeCollapse = undefined;
            }

            this.isCollapsed.set(false);
            this._saveCollapsedState(false);
        } else {
            this._heightBeforeCollapse = this.heightPx();
            this.heightFraction.set(0); // CSS .widget.collapsed handles the fixed 36px
            this.isCollapsed.set(true);
            this._saveCollapsedState(true);
        }
    }

    private _loadSettingsFromStorage(): void {
        // Fixed-size widgets have a constant px height — no fraction to load or persist.
        if (this.fixedSize()) return;

        const isCollapsed = this._safeGet(KEY_COLLAPSED(this.widgetId()));
        if (isCollapsed != undefined) this.isCollapsed.set(isCollapsed === 'true');

        if (this.isCollapsed()) this._restoreHeightBeforeCollapse();

        const isFillRemaining = this._safeGet(KEY_FILL_REMAINING(this.widgetId()));
        if (isFillRemaining != undefined) {
            this.fillRemaining.set(isFillRemaining === 'true');
            if (this.fillRemaining() && !this.isCollapsed()) {
                // Only release to flex sizing when not also collapsed
                this.heightFraction.set(0);
                this._heightBeforeCollapse = undefined;
                return;
            }
        }

        if (this.isCollapsed()) {
            // CSS .widget.collapsed handles the fixed header height — no fraction needed
            return;
        }

        const fraction = this._loadHeightFraction();
        this.heightFraction.set(fraction > 0 ? fraction : this.defaultFraction());
    }

    private _restoreHeightBeforeCollapse(): void {
        const colH = this._getColumnHeight();
        const prev = this._safeGet(KEY_PREV_HEIGHT(this.widgetId()));
        if (prev) {
            const fraction = Number.parseFloat(prev);
            if (!Number.isNaN(fraction) && fraction > 0) {
                this._heightBeforeCollapse = Math.round(fraction * colH);
            }
        } else {
            const f = this._loadHeightFraction();
            const effectiveFraction = f > 0 ? f : this.defaultFraction();
            this._heightBeforeCollapse = Math.round(effectiveFraction * colH);
        }
    }

    private _saveCollapsedState(collapsed: boolean): void {
        const id = this.widgetId();
        if (!id) return;
        if (collapsed) {
            this._safeSet(KEY_COLLAPSED(id), 'true');
            if (this._heightBeforeCollapse != undefined) {
                const colH = this._getColumnHeight();
                if (colH > 0) {
                    this._safeSet(KEY_PREV_HEIGHT(id), String(this._heightBeforeCollapse / colH));
                }
            }
        } else {
            this._safeSet(KEY_COLLAPSED(id), 'false');
            this._safeSet(KEY_PREV_HEIGHT(id), '0');
        }
    }

    private _setFillRemaining(fillRemaining: boolean): void {
        const id = this.widgetId();
        this.fillRemaining.set(fillRemaining);
        if (fillRemaining) this.heightFraction.set(0);
        this._safeSet(KEY_FILL_REMAINING(id), String(fillRemaining));
    }

    private _saveHeight(): void {
        const id = this.widgetId();
        if (!id || this.fillRemaining() || this.fixedSize()) return;
        this._safeSet(KEY_HEIGHT(id), String(this.heightFraction()));
    }

    private _loadHeightFraction(): number {
        const id = this.widgetId();
        if (id) {
            const saved = this._safeGet(KEY_HEIGHT(id));
            if (saved != undefined) {
                const fraction = Number.parseFloat(saved);
                if (!Number.isNaN(fraction) && fraction > 0) return fraction;
            }
        }
        return 0;
    }

    private _setHeightFromPx(px: number): void {
        if (px <= 0) { this.heightFraction.set(0); return; }
        const colH = this._getColumnHeight();
        this.heightFraction.set(colH > 0 ? px / colH : 0);
    }

    private _safeSet(key: string, value: string): void {
        try {
            localStorage.setItem(key, value);
        } catch (e: any) {
            if (!this._storageErrorShown) {
                const msg = e?.message ? e.message : 'Unable to access localStorage.';
                try { this._notification.openText(`Error saving preferences: ${msg}`, 3000); } catch {}
                this._storageErrorShown = true;
            }
        }
    }

    private _safeGet(key: string): string {
        try {
            return localStorage.getItem(key);
        } catch (e: any) {
            if (!this._storageErrorShown) {
                const msg = e?.message ? e.message : 'Unable to access localStorage.';
                try { this._notification.openText(`Error reading preferences: ${msg}`, 3000); } catch {}
                this._storageErrorShown = true;
            }
            return undefined;
        }
    }

    private _handleDragDown(delta: number, me: number, start: number[], widgets: BaseWidgetDirective[], newHeights: number[]): void {
        // Non-fixedSize: grow current widget, shrink widgets below as much as possible.
        // If widgets below are already at min-height the column overflows and becomes scrollable.
        newHeights[me] = start[me] + delta;
        this._applyPhase1Shrink(delta, me, start, widgets, newHeights);
    }

    private _applyPhase1Shrink(phase1Growth: number, me: number, start: number[], widgets: BaseWidgetDirective[], newHeights: number[]): void {
        let toShrink = phase1Growth;
        for (let i = me + 1; i < widgets.length && toShrink > 0; i++) {
            if (widgets[i].fixedSize()) continue;
            const canShrink = Math.max(0, start[i] - HEADER_HEIGHT_PX);
            const shrink = Math.min(canShrink, toShrink);
            newHeights[i] = start[i] - shrink;
            toShrink -= shrink;
        }
    }

    private _handleDragUp(delta: number, me: number, start: number[], widgets: BaseWidgetDirective[], newHeights: number[]): void {
        // Drag up: cascade-shrink upward from me; freed space grows widgets below sequentially
        const totalMove = -delta;

        let remaining = totalMove;
        for (let i = me; i >= 0 && remaining > 0; i--) {
            if (widgets[i].fixedSize()) continue;
            const canShrink = start[i] - HEADER_HEIGHT_PX;
            const shrink = Math.min(canShrink, remaining);
            newHeights[i] = start[i] - shrink;
            remaining -= shrink;
        }

        const actualFreed = totalMove - remaining;
        let toGrow = actualFreed;
        for (let i = me + 1; i < widgets.length && toGrow > 0; i++) {
            if (widgets[i].fixedSize()) continue;
            const maxHeight = widgets[i].fixedSize() ? widgets[i].defaultHeightPx() : Infinity;
            const canGrow = Math.max(0, maxHeight - start[i]);
            const grow = Math.min(canGrow, toGrow);
            newHeights[i] = start[i] + grow;
            toGrow -= grow;
        }
    }

    private _getColumnHeight(): number {
        // parentElement is the component host (display:contents) — skip it to reach the column.
        const parent = (this._el?.nativeElement as HTMLElement)?.parentElement?.parentElement;
        if (!parent) return 0;
        // flex-basis % resolves against the container's content-box, so subtract
        // vertical padding from the border-box height to get the correct denominator.
        const cs = getComputedStyle(parent);
        const h = parent.getBoundingClientRect().height
            - (Number.parseFloat(cs.paddingTop) || 0)
            - (Number.parseFloat(cs.paddingBottom) || 0);
        return Math.round(h);
    }
}
